import {
  getRouteTemplateForTrackedPath,
  trackClick,
} from './google-analytics';

const BUTTON_SELECTOR =
  'button:not([disabled]), input[type="button"]:not([disabled]), input[type="submit"]:not([disabled]), [role="button"]:not([aria-disabled="true"])';

const MAX_ACTION_LENGTH = 100;
const MAX_LABEL_LENGTH = 64;

export function isTrackedRoutePathname(pathname: string): boolean {
  return (
    pathname.startsWith('/patients') ||
    pathname.startsWith('/hub') ||
    pathname.startsWith('/mobile') ||
    pathname.startsWith('/specialist') ||
    pathname.startsWith('/estimate')
  );
}

export function getTrackedAppAreaFromPathname(pathname: string): string | null {
  if (pathname.startsWith('/patients')) {
    return 'patients';
  }
  if (pathname.startsWith('/hub')) {
    return 'payment_hub';
  }
  if (pathname.startsWith('/mobile')) {
    return 'payment_hub_mobile';
  }
  if (pathname.startsWith('/specialist')) {
    return 'specialist';
  }
  if (pathname.startsWith('/estimate')) {
    return 'estimate';
  }
  return null;
}

function sanitizeAction(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, MAX_ACTION_LENGTH);
}

function isSafeButtonLabel(label: string): boolean {
  const text = label.trim();
  if (!text || text.length > MAX_LABEL_LENGTH) {
    return false;
  }
  if (text.includes('@')) {
    return false;
  }
  return true;
}

function readTestId(element: Element): string | null {
  const testId = element.getAttribute('test-id');
  if (!testId) {
    return null;
  }

  if (testId.startsWith('button-wrapper-')) {
    return testId.slice('button-wrapper-'.length);
  }

  if (testId.startsWith('button-spinner-')) {
    return null;
  }

  return testId;
}

export function resolveButtonClickAction(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const button = target.closest(BUTTON_SELECTOR);
  if (!button) {
    return null;
  }

  const explicitAction = button.getAttribute('data-analytics-action');
  if (explicitAction) {
    return sanitizeAction(explicitAction);
  }

  const buttonTestId = readTestId(button);
  if (buttonTestId) {
    return sanitizeAction(buttonTestId);
  }

  const labeledAncestor = button.closest('[test-id], [aria-label]');
  if (labeledAncestor && labeledAncestor !== button) {
    const ancestorTestId = readTestId(labeledAncestor);
    if (ancestorTestId) {
      return sanitizeAction(ancestorTestId);
    }

    const ariaLabel = labeledAncestor.getAttribute('aria-label');
    if (ariaLabel) {
      return sanitizeAction(ariaLabel);
    }
  }

  const ariaLabel = button.getAttribute('aria-label');
  if (ariaLabel) {
    return sanitizeAction(ariaLabel);
  }

  const name = button.getAttribute('name');
  if (name) {
    return sanitizeAction(name);
  }

  const label = button.textContent ?? '';
  if (isSafeButtonLabel(label)) {
    return sanitizeAction(label);
  }

  return null;
}

export function trackButtonClickFromEvent(pathname: string, target: EventTarget | null) {
  if (!isTrackedRoutePathname(pathname)) {
    return;
  }

  const action = resolveButtonClickAction(target);
  if (!action) {
    return;
  }

  const appArea = getTrackedAppAreaFromPathname(pathname);
  const routeTemplate = getRouteTemplateForTrackedPath(pathname);

  trackClick(action, {
    ...(appArea ? { app_area: appArea } : {}),
    ...(routeTemplate ? { route_template: routeTemplate, page_path: routeTemplate } : {}),
  });
}
